import type { MaybeRef, MaybeRefOrGetter } from 'vue';
import { toValue, computed } from 'vue';
import type { SelectItem, SelectItemSeparator } from './types';
import useListHoverController from './use-list-hover-controller';

type BaseOptions<T extends SelectItem> = {
  items: MaybeRefOrGetter<Readonly<(T | SelectItemSeparator)[]>>;
  required?: boolean;
  modelValue: MaybeRefOrGetter<NoInfer<T['id']> | null>;
  onChange: (value: T['id'] | null, item?: T) => void;
  useKeyboardEvents?: MaybeRef<boolean>;
};

/**
 * Хук для управления `Одиночным` выбором элементов в списке.
 *
 * Для множественного выбора используйте {@link useListMultipleSelect}
 */

const useListSelect = <T extends SelectItem>({
  items,
  required,
  modelValue,
  onChange,
  useKeyboardEvents
}: BaseOptions<T>) => {
  const toggle = (item: T) => {
    const isSelected = toValue(modelValue) === item.id;

    if (!required && isSelected) {
      clear();
    } else {
      onChange?.(item.id, item);
    }
  };

  const clear = () => {
    onChange?.(null);
  };

  // User selection handling
  const { onHover, isHovered, hoveredIndex, hoveredFromKeyboard } = useListHoverController<T>({
    items,
    toggle,
    listenKeyboard: useKeyboardEvents
  });

  // Data
  const selectableItems = computed(() => {
    return toValue(items).map((item: T | SelectItemSeparator, index: number) => ({
      ...item,
      userSelection: {
        selected: 'id' in item ? toValue(modelValue) === item.id : false,
        hovered: isHovered(index)
      }
    }));
  });

  return {
    onHover,
    toggle,
    clear,
    hoveredIndex,
    hoveredFromKeyboard,
    selectableItems
  };
};

export default useListSelect;
